type PageItem = number | 'ellipsis';

/**
 * Builds a compact list of page numbers with ellipses so the control stays a
 * fixed width no matter how many pages exist. Always keeps the first and last
 * page visible, plus a window of `siblingCount` pages on each side of the
 * current page.
 */
export function getPaginationRange(
  currentPage: number,
  totalPages: number,
  siblingCount = 1
): PageItem[] {
  // first + last + current + 2 ellipses + siblings on both sides
  const totalPageNumbers = siblingCount * 2 + 5;

  if (totalPages <= totalPageNumbers) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const leftSibling = Math.max(currentPage - siblingCount, 1);
  const rightSibling = Math.min(currentPage + siblingCount, totalPages);

  const showLeftEllipsis = leftSibling > 2;
  const showRightEllipsis = rightSibling < totalPages - 1;

  if (!showLeftEllipsis && showRightEllipsis) {
    const leftCount = 3 + 2 * siblingCount;
    const leftRange = Array.from({ length: leftCount }, (_, i) => i + 1);
    return [...leftRange, 'ellipsis', totalPages];
  }

  if (showLeftEllipsis && !showRightEllipsis) {
    const rightCount = 3 + 2 * siblingCount;
    const rightRange = Array.from(
      { length: rightCount },
      (_, i) => totalPages - rightCount + 1 + i
    );
    return [1, 'ellipsis', ...rightRange];
  }

  const middleRange = Array.from(
    { length: rightSibling - leftSibling + 1 },
    (_, i) => leftSibling + i
  );
  return [1, 'ellipsis', ...middleRange, 'ellipsis', totalPages];
}

const ACCENTS = {
  blue: {
    active:
      'z-10 bg-blue-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600',
    inactive:
      'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0',
    ellipsis: 'text-gray-700 ring-1 ring-inset ring-gray-300',
    arrow:
      'text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50',
  },
  indigo: {
    active: 'z-10 bg-indigo-50 border border-indigo-500 text-indigo-600',
    inactive: 'bg-white border border-gray-300 text-gray-500 hover:bg-gray-50',
    ellipsis: 'bg-white border border-gray-300 text-gray-500',
    arrow:
      'bg-white border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-300',
  },
} as const;

interface PaginationProps {
  page: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  /** Pages shown on each side of the current page (default 1). */
  siblingCount?: number;
  accent?: keyof typeof ACCENTS;
  /** Outer wrapper classes. Defaults to a rounded white card. */
  className?: string;
}

export default function Pagination({
  page,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  siblingCount = 1,
  accent = 'blue',
  className = 'flex items-center justify-between bg-white px-4 py-3 sm:px-6 rounded-2xl border border-gray-100 shadow-sm',
}: PaginationProps) {
  if (totalCount <= 0) return null;

  const styles = ACCENTS[accent];
  const goTo = (p: number) => onPageChange(Math.min(Math.max(p, 1), totalPages));
  const items = getPaginationRange(page, totalPages, siblingCount);

  return (
    <div className={className}>
      {/* Mobile: simple prev/next */}
      <div className="flex flex-1 justify-between sm:hidden">
        <button
          onClick={() => goTo(page - 1)}
          disabled={page === 1}
          className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Previous
        </button>
        <button
          onClick={() => goTo(page + 1)}
          disabled={page === totalPages || totalPages === 0}
          className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Next
        </button>
      </div>

      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            Showing <span className="font-medium">{(page - 1) * pageSize + 1}</span> to{' '}
            <span className="font-medium">{Math.min(page * pageSize, totalCount)}</span> of{' '}
            <span className="font-medium">{totalCount}</span> results
          </p>
        </div>
        {totalPages > 1 && (
          <div>
            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              <button
                onClick={() => goTo(page - 1)}
                disabled={page === 1}
                className={`relative inline-flex items-center rounded-l-md px-2 py-2 ${styles.arrow}`}
              >
                <span className="sr-only">Previous</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                </svg>
              </button>

              {items.map((item, i) =>
                item === 'ellipsis' ? (
                  <span
                    key={`ellipsis-${i}`}
                    className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${styles.ellipsis}`}
                    aria-hidden="true"
                  >
                    &hellip;
                  </span>
                ) : (
                  <button
                    key={item}
                    onClick={() => goTo(item)}
                    aria-current={page === item ? 'page' : undefined}
                    className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${page === item ? styles.active : styles.inactive}`}
                  >
                    {item}
                  </button>
                )
              )}

              <button
                onClick={() => goTo(page + 1)}
                disabled={page === totalPages}
                className={`relative inline-flex items-center rounded-r-md px-2 py-2 ${styles.arrow}`}
              >
                <span className="sr-only">Next</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                </svg>
              </button>
            </nav>
          </div>
        )}
      </div>
    </div>
  );
}
